import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TypedMemo } from '../../../common/types';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../common/views/LoadingView';
import { useGetCatalogQuery } from '../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';
import { localDay } from '../releases/plan/planMode';
import { useFailuresSeen } from '../work/assetsFailuresSeen';
import { useArchivePostFiles } from './hooks/useArchivePostFiles';
import { usePostFileActions } from './hooks/usePostFileActions';
import { usePostsBoard } from './hooks/usePostsBoard';
import { usePostsFilters } from './hooks/usePostsFilters';
import { usePostsSelection } from './hooks/usePostsSelection';
import {
  boardFiles,
  emptyBoardPlanTarget,
  isBoardEmpty,
  PostsBoardFile,
  PostsLaneId,
  selectableFiles,
} from './postsModel';
import { PostsLaneProps } from './postsLaneProps';
import { PostsArchiveNotice } from './PostsArchiveNotice';
import { PostsArchiveView } from './PostsArchiveView';
import { PostsEmpty } from './PostsEmpty';
import { PostsFileViewer } from './PostsFileViewer';
import { PostsMakingStrip } from './PostsMakingStrip';
import { PostsPhoneTabs } from './PostsPhoneTabs';
import { PostsDemoNote } from './PostsDemoNote';
import { PostsPostedLane } from './PostsPostedLane';
import { PostsReadyLane } from './PostsReadyLane';
import { PostsScheduledLane } from './PostsScheduledLane';
import { PostsSelectBar } from './PostsSelectBar';
import { PostsToolbar } from './PostsToolbar';
import { WatermarkBanner } from '../../watermarkUpsell/files/WatermarkBanner';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Posts.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface PostsBoardProps {
  accountId: number;
  /** The release page Posts tab: the post files of one song. Absent: every song (the Posts page). */
  catalogItemUuid?: string;
}

/**
 * The Posts board (design assets-rethink c1, c2, c3, d2): the Making strip, then the lanes Ready to
 * post, Scheduled and Posted; Select and Archive; the archive view (`?view=archive`). On the Posts
 * page it reads the song filter from the address; on the release Posts tab the song is set.
 */
const PostsBoardComponent: React.FC<PostsBoardProps> = ({ accountId, catalogItemUuid }) => {
  const isReleaseTab = catalogItemUuid !== undefined;
  const filters = usePostsFilters(catalogItemUuid);
  const isDemo = useIsDemoAccount();
  const catalog = useGetCatalogQuery({ accountId });
  const catalogItems = useMemo<CatalogItem[]>(() => catalog.data?.items ?? [], [catalog.data]);
  const catalogByUuid = useMemo(() => new Map(catalogItems.map((item) => [item.uuid, item])), [catalogItems]);
  const { board, isLoading, error, refetch } = usePostsBoard({
    accountId,
    catalogItemUuid: filters.catalogItemUuid,
    platform: filters.platform,
  });
  const readOnly = isDemo || board?.readOnly === true;
  const actions = usePostFileActions(catalogItems);
  const archiver = useArchivePostFiles(accountId);
  const pickable = useMemo(() => (board === undefined ? [] : selectableFiles(board)), [board]);
  const selection = usePostsSelection(pickable);
  const [phoneLane, setPhoneLane] = useState<PostsLaneId>('ready');
  const now = useMemo(() => new Date(), []);

  // The Posts menu badge counts the failures: the Posts page that shows one marks it seen.
  const { markSeen } = useFailuresSeen(accountId);
  const failedShown = useMemo(() => board?.making.filter((file) => file.state === 'failed').length ?? 0, [board]);
  useEffect(() => {
    if (!isReleaseTab && failedShown > 0) {
      markSeen();
    }
  }, [failedShown, isReleaseTab, markSeen]);

  const { run } = archiver;
  const archiveFiles = useCallback(
    (files: ReadonlyArray<PostsBoardFile>) => run(files, true).catch(console.error),
    [run]
  );
  const { stop: stopSelect } = selection;
  const archivePicked = useCallback(() => {
    archiveFiles(selection.picked).finally(stopSelect).catch(console.error);
  }, [archiveFiles, selection.picked, stopSelect]);
  const { setAsset, setView } = filters;
  const openFile = useCallback((file: PostsBoardFile) => setAsset(file.uuid), [setAsset]);
  const planRollout = useCallback(() => {
    const target = emptyBoardPlanTarget(filters.catalogItemUuid, catalogItems, localDay(new Date()));
    if (target.kind === 'plan') {
      actions.openPlan(target.catalogItemUuid);
      return;
    }
    actions.addSong();
  }, [actions, catalogItems, filters.catalogItemUuid]);
  const { openReleasePosts } = actions;
  const seeMaking = useCallback(
    (file: PostsBoardFile) => {
      if (file.catalogItemUuid !== null) {
        openReleasePosts(file.catalogItemUuid);
      }
    },
    [openReleasePosts]
  );
  const viewerFiles = useMemo(() => (board === undefined ? [] : boardFiles(board)), [board]);

  if (filters.view === 'archive') {
    return (
      <PostsArchiveView
        accountId={accountId}
        catalogItemUuid={filters.catalogItemUuid}
        outcome={filters.outcome}
        onOutcome={filters.setOutcome}
        onBack={() => setView('board')}
        onOpen={openFile}
        openAsset={filters.asset}
        onCloseAsset={() => setAsset(undefined)}
      />
    );
  }
  if (isLoading) {
    return <LoadingView />;
  }
  if (board === undefined) {
    return <ErrorView error={error as Error} retry={refetch} />;
  }

  const laneProps: PostsLaneProps = {
    songs: board.songs,
    now,
    actions,
    readOnly,
    selectionOf: (file) =>
      readOnly
        ? null
        : { isOn: selection.isOn, isPicked: selection.isPicked(file), toggle: () => selection.toggle(file) },
    onOpen: openFile,
    onArchive: (file) => {
      archiveFiles([file]).catch(console.error);
    },
    isHiddenOnPhone: false,
  };
  const counts = { ready: board.ready.length, scheduled: board.scheduled.length, posted: board.posted.length };

  return (
    <div className={styles.board} data-testid="posts-board">
      {/* design: trial-upsell a4. The banner replaced the plan note; the demo artist keeps its line.
          On a phone it goes under the lanes, so the first post is above the fold (QA mob-14). */}
      <div className={styles.boardNote}>{readOnly ? <PostsDemoNote /> : <WatermarkBanner />}</div>
      <PostsToolbar
        songs={isReleaseTab ? undefined : catalogItems}
        song={filters.catalogItemUuid}
        onSong={filters.setSong}
        platform={filters.platform}
        onPlatform={filters.setPlatform}
        onSelect={readOnly ? null : selection.start}
        isSelecting={selection.isOn}
        archivedCount={board.archivedCount}
        onArchive={() => setView('archive')}
      />
      <PostsArchiveNotice
        blocked={archiver.blocked}
        failed={archiver.failed}
        onOpenPost={actions.openCalendar}
        onClose={archiver.clear}
      />
      {selection.isOn && (
        <PostsSelectBar
          pickedCount={selection.picked.length}
          totalCount={pickable.length}
          isBusy={archiver.isBusy}
          onPickAll={selection.pickAll}
          onArchive={archivePicked}
          onDone={stopSelect}
        />
      )}
      {isBoardEmpty(board) ? (
        <PostsEmpty readOnly={readOnly} onPlanRollout={planRollout} onNewPost={actions.newPost} />
      ) : (
        <>
          <PostsMakingStrip
            accountId={accountId}
            files={board.making}
            songs={board.songs}
            catalogByUuid={catalogByUuid}
            readOnly={readOnly}
            onSee={seeMaking}
          />
          <PostsPhoneTabs lane={phoneLane} counts={counts} onChange={setPhoneLane} />
          <div className={styles.lanes}>
            {/* The three lanes take the same shared props (`PostsLaneProps`). */}
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <PostsReadyLane {...laneProps} files={board.ready} isHiddenOnPhone={phoneLane !== 'ready'} />
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <PostsScheduledLane {...laneProps} rows={board.scheduled} isHiddenOnPhone={phoneLane !== 'scheduled'} />
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <PostsPostedLane {...laneProps} rows={board.posted} isHiddenOnPhone={phoneLane !== 'posted'} />
          </div>
        </>
      )}
      <PostsFileViewer
        accountId={accountId}
        assetUuid={filters.asset}
        files={viewerFiles}
        onClose={() => setAsset(undefined)}
      />
    </div>
  );
};

export const PostsBoard = TypedMemo(PostsBoardComponent);
